import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/shared/components/ui/accordion";
import { Button, Checkbox, Label } from "@/shared/components/ui";
import LoadingSpinner from "@/shared/components/LoadingSpinner";
import { InputWithLabel } from "@/shared/components/ui/InputWithLabel";
import { SelectWithLabel } from "@/shared/components/ui/SelectWithLabel";
import { RadioButtonGroup } from "@/shared/components/ui/RadioButtonGroup";
import { FormField } from "@/shared/components/FormField";
import { Quote } from "@/shared/types/Quote.interface";
import { cn } from "@/shared/utils";
import { CA_PROVINCES } from "@/shared/utils/constants";
import Strings from "@/shared/utils/Strings.constants";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { useBillingFrequency } from "@/shared/hooks/useBillingFrequency";
import { useCheckoutValidation } from "@/quote-ptz-ca/src/hooks/useCheckoutValidation";
import { CaTerms } from "./CaTerms";
import { ChasePaymentIframe } from "./ChasePaymentIframe";
import DollarIcon from "../../../../src/media/icons/dollars-icon.svg";

const TERMS_STEP = "terms";
const PAYMENT_STEP = "payment";

/** Numbered step marker that flips to a green check once the step is complete (matches the onboarding widget). */
function StepIcon({ complete, number }: { complete: boolean; number: number }) {
    if (complete) {
        return <FontAwesomeIcon icon={faCircleCheck} className="size-6 text-content-success" />;
    }
    return <span className="flex size-6 items-center justify-center rounded-full bg-background-brand-quaternary text-xs font-bold text-content-brand">{number}</span>;
}

/**
 * Two-step checkout accordion (onboarding-widget style):
 *   1. Terms & Conditions — mobile number (if not already captured) + terms acceptance
 *   2. Payment — the Chase HPF iframe
 *
 * The Terms step is open by default; once it's complete (valid phone persisted + terms accepted)
 * the accordion auto-advances to Payment. Mounting the iframe only when its panel opens also means
 * the single-use uid is minted right when the customer reaches payment.
 */
export function CheckoutSteps({ quote }: { quote: Quote }) {
    const { isQuoteUpdating } = useAppLayerContext().appState;
    const {
        phone,
        showPhoneError,
        handlePhoneChange,
        termsAccepted,
        termsError,
        handleTermsChange,
        isTermsComplete,
        saveError,
        isSaving,
        submitStep,
        paperless,
        handlePaperlessChange,
        address,
        addressErrors,
        handleAddressChange,
        handleProvinceChange
    } = useCheckoutValidation(quote);

    // Billing frequency state + handler (shared with PTZ-US)
    const { selectedBillingFrequency, frequencyToDisplay, showTransactionFee, annualSavings, handleFrequencyChange } = useBillingFrequency(quote);

    const [openStep, setOpenStep] = useState<string>(TERMS_STEP);
    const prevTermsCompleteRef = useRef(false);

    // Open Payment whenever the Terms step transitions from incomplete → complete (edge-triggered, not a
    // one-shot). The paperless flow re-locks the step — complete, then uncheck paperless, then fill the
    // mailing address — so the customer completes it a second time; a one-shot would leave them stuck on
    // Terms with Payment merely unblocked. We only advance on the transition, so manually reopening Terms
    // while it stays complete doesn't yank them forward.
    useEffect(() => {
        if (isTermsComplete && !prevTermsCompleteRef.current) {
            setOpenStep(PAYMENT_STEP);
        }
        prevTermsCompleteRef.current = isTermsComplete;
    }, [isTermsComplete]);

    return (
        <Accordion type="single" collapsible value={openStep} onValueChange={setOpenStep} className="flex flex-col gap-3">
            <AccordionItem value={TERMS_STEP} className="rounded-lg border border-stroke-primary bg-background-primary px-4">
                <AccordionTrigger className="w-full hover:no-underline" wrapperAsChild>
                    <div className="flex items-center gap-3">
                        <StepIcon complete={isTermsComplete} number={1} />
                        <span className="text-base font-bold text-content-primary">Terms and Conditions</span>
                    </div>
                </AccordionTrigger>
                <AccordionContent className="px-2 py-4">
                    <div className="relative flex flex-col gap-5">
                        {/* Dim + lock the inputs while a persist is in flight; the overlay spinner signals the round-trip. */}
                        <div className={cn("flex flex-col gap-5", isSaving && "pointer-events-none opacity-50")}>
                            <FormField error={showPhoneError ? Strings.ERRORS.PHONE : undefined} errorId="error-ca-phone">
                                <InputWithLabel
                                    variant="floating-inner"
                                    label={Strings.MOBILE_PHONE}
                                    error={showPhoneError ? Strings.ERRORS.PHONE : undefined}
                                    inputProps={{
                                        id: "ca-phone",
                                        type: "tel",
                                        autoComplete: "tel-national",
                                        value: phone,
                                        disabled: isSaving,
                                        onChange: e => handlePhoneChange(e.target.value)
                                    }}
                                />
                            </FormField>
                            <CaTerms checked={termsAccepted} onCheckedChange={handleTermsChange} disabled={isSaving} error={termsError} />

                            {/* Paperless opt-in (checked by default). Unchecking reveals the mailing address. */}
                            <FormField className="flex-row gap-3">
                                <Checkbox
                                    id="paperless-consent"
                                    data-testid="paperless-consent"
                                    aria-labelledby="paperless-consent-label"
                                    checked={paperless}
                                    disabled={isSaving}
                                    onCheckedChange={(value: boolean) => handlePaperlessChange(value)}
                                />
                                <Label id="paperless-consent-label" htmlFor="paperless-consent" className="flex flex-col bg-background-transparent" allowLabelClick>
                                    <span className="text-sm text-content-primary">{Strings.PTZ_CA.PAPERLESS.LABEL}</span>
                                    <span className="text-xs text-content-secondary">
                                        {paperless ? Strings.PTZ_CA.PAPERLESS.CHECKED_HINT : Strings.PTZ_CA.PAPERLESS.UNCHECKED_HINT}
                                    </span>
                                </Label>
                            </FormField>

                            {!paperless && (
                                <div className="flex flex-col gap-4" data-testid="ca-mailing-address">
                                    <p className="font-bold">{Strings.PTZ_CA.MAILING_ADDRESS.HEADER}</p>
                                    <FormField error={addressErrors.street1} errorId="error-ca-street1">
                                        <InputWithLabel
                                            variant="floating-inner"
                                            label={Strings.PTZ_CA.MAILING_ADDRESS.ADDRESS_LINE_1}
                                            error={addressErrors.street1}
                                            inputProps={{
                                                id: "ca-street1",
                                                autoComplete: "address-line1",
                                                placeholder: " ",
                                                value: address.street1,
                                                disabled: isSaving,
                                                onChange: e => handleAddressChange("street1", e.target.value),
                                                maxLength: 50,
                                                "aria-describedby": addressErrors.street1 ? "error-ca-street1" : undefined
                                            }}
                                        />
                                    </FormField>
                                    <FormField error={addressErrors.street2} errorId="error-ca-street2">
                                        <InputWithLabel
                                            variant="floating-inner"
                                            label={Strings.PTZ_CA.MAILING_ADDRESS.ADDRESS_LINE_2}
                                            error={addressErrors.street2}
                                            inputProps={{
                                                autoComplete: "address-line2",
                                                placeholder: " ",
                                                value: address.street2,
                                                disabled: isSaving,
                                                onChange: e => handleAddressChange("street2", e.target.value),
                                                maxLength: 50
                                            }}
                                        />
                                    </FormField>
                                    <FormField error={addressErrors.city} errorId="error-ca-city">
                                        <InputWithLabel
                                            variant="floating-inner"
                                            label={Strings.PTZ_CA.MAILING_ADDRESS.CITY}
                                            error={addressErrors.city}
                                            inputProps={{
                                                id: "ca-city",
                                                autoComplete: "address-level2",
                                                placeholder: " ",
                                                value: address.city,
                                                disabled: isSaving,
                                                onChange: e => handleAddressChange("city", e.target.value),
                                                maxLength: 50,
                                                "aria-describedby": addressErrors.city ? "error-ca-city" : undefined
                                            }}
                                        />
                                    </FormField>
                                    <FormField error={addressErrors.province} errorId="error-ca-province">
                                        <SelectWithLabel
                                            variant="floating-inner"
                                            id="ca-province"
                                            label={Strings.PTZ_CA.MAILING_ADDRESS.PROVINCE}
                                            placeholder={Strings.PTZ_CA.MAILING_ADDRESS.PROVINCE}
                                            value={address.province || undefined}
                                            options={CA_PROVINCES}
                                            onValueChange={handleProvinceChange}
                                            error={addressErrors.province}
                                            disabled={isSaving}
                                        />
                                    </FormField>
                                    <FormField error={addressErrors.postal} errorId="error-ca-postal">
                                        <InputWithLabel
                                            variant="floating-inner"
                                            label={Strings.PTZ_CA.MAILING_ADDRESS.POSTAL_CODE}
                                            error={addressErrors.postal}
                                            inputProps={{
                                                id: "ca-postal",
                                                autoComplete: "postal-code",
                                                placeholder: " ",
                                                value: address.postal,
                                                disabled: isSaving,
                                                onChange: e => handleAddressChange("postal", e.target.value),
                                                maxLength: 7,
                                                "aria-describedby": addressErrors.postal ? "error-ca-postal" : undefined
                                            }}
                                        />
                                    </FormField>
                                </div>
                            )}
                            {saveError && (
                                <p className="text-sm text-content-danger" role="alert">
                                    We couldn’t save your information. Please try again.
                                </p>
                            )}
                            {/* Always enabled — clicking validates and surfaces errors when invalid, and on
                                success persists the step and advances to Payment. Sits inside the dimmed
                                wrapper, so it's non-interactive (not disabled) while a save is in flight. */}
                            <Button
                                className="w-full rounded-full focus:rounded-full"
                                onClick={async () => {
                                    if (await submitStep()) setOpenStep(PAYMENT_STEP);
                                }}
                            >
                                {Strings.PTZ_CA.CHECKOUT_CONTINUE}
                            </Button>
                        </div>
                        {isSaving && (
                            <div className="absolute inset-0 flex items-center justify-center" role="status" aria-label="Saving">
                                <LoadingSpinner />
                            </div>
                        )}
                    </div>
                </AccordionContent>
            </AccordionItem>

            <AccordionItem value={PAYMENT_STEP} className="rounded-lg border border-stroke-primary bg-background-primary px-4">
                <AccordionTrigger
                    // Locked until Terms is complete; auto-advance opens it once that happens.
                    disabled={!isTermsComplete}
                    className="w-full hover:no-underline disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:no-underline"
                    wrapperAsChild
                >
                    <span className="flex items-center gap-3">
                        <StepIcon complete={false} number={2} />
                        <span className="text-base font-bold text-content-primary">Payment</span>
                    </span>
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-2 pb-0">
                    <div data-testid="billing-frequency-section" className={cn(showTransactionFee ? "rounded-xl bg-background-success px-3 pb-1 pt-2" : "")}>
                        <RadioButtonGroup
                            variant="floating-inner"
                            key={selectedBillingFrequency}
                            onValueChange={async value => await handleFrequencyChange(value as "monthly" | "yearly")}
                            initialValue={selectedBillingFrequency}
                            aria-labelledby="billing-frequency"
                            className={cn("bg-background-weakest", showTransactionFee && "rounded-xl border border-stroke-success-light")}
                            disabled={isQuoteUpdating}
                            radioGroupItemClassName={
                                showTransactionFee
                                    ? "!border-0 data-[state=on]:!border-2 data-[state=on]:!border-stroke-brand-b"
                                    : "data-[state=off]:!border data-[state=off]:!border-stroke-primary"
                            }
                            options={[
                                { label: "Billed Monthly", value: "monthly" },
                                { label: "Billed Annually", value: "yearly" }
                            ]}
                        />
                        {showTransactionFee && (
                            <div className="flex flex-row items-center justify-center gap-1 pt-2">
                                {frequencyToDisplay === "yearly" ? <FontAwesomeIcon icon={faCircleCheck} className="size-5 text-content-primary" /> : <DollarIcon />}
                                <span className="text-xs font-semibold text-content-primary">
                                    {frequencyToDisplay === "yearly" ? `You're saving $${annualSavings}/yr!` : `Save $${annualSavings}/yr with annual billing.`}
                                </span>
                            </div>
                        )}
                    </div>
                    <ChasePaymentIframe quote={quote} disabled={isQuoteUpdating} />
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}
